import useSWR, { mutate } from 'swr';

const API_BASE = process.env.NEXT_PUBLIC_API_URL || '';

// ── Variable Sources: discriminated union types ──────────────────────
// Each content_type stores its metadata in variable_sources with a specific shape.

/** Litany: multiple sections with refrain + text */
export interface LitanySources {
  type: 'litany';
  sections: Array<{ refrain: string; text: string }>;
}

/** Exclamation: priest exclamation text + choir response */
export interface ExclamationSources {
  response: string;
  text: string;
}

/** Psalm: verses stored as multi-line text */
export interface PsalmSources {
  verses: string;
}

/** Prayer: text + secret flag */
export interface PrayerSources {
  is_secret: boolean;
  text: string;
}

/** Hymn / Reading / other variable blocks: source metadata */
export interface SourceMetadata {
  source?: string;
  type?: string;
  subtype?: string;
}

/** Antiphon: position + antiphon_type resolved by assembler */
export interface AntiphonSources {
  type: 'antiphon';
  position: 1 | 2 | 3;
  antiphon_type?: 'daily' | 'festal' | 'typical';
}

/** Entrance verse: type marker, variant resolved by assembler */
export interface EntranceVerseSources {
  type: 'entrance_verse';
}

/** Trisagion variant: type marker, variant resolved by assembler */
export interface TrisagionSources {
  type: 'trisagion';
}

/** Cherubic hymn: type marker, variant resolved by assembler */
export interface CherubicSources {
  type: 'cherubic_hymn';
}

/** Megalynarion: type marker + optional feast reference */
export interface MegalynarionSources {
  type: 'megalynarion';
  feast?: string;
}

/** Communion verse: type marker */
export interface CommunionVerseSources {
  type: 'communion_verse';
}

/** God the Lord / Alleluia: type marker */
export interface GodTheLordSources {
  type: 'god_the_lord';
}

/** Stepenna: tone 1-8 */
export interface StepennaSources {
  type: 'stepenna';
  tone?: number;
}

/** Prokimen: type + optional position */
export interface ProkimenSources {
  type: 'prokimen';
  position?: number;
}

/** Great prokimen: type + position (vespers/presanctified) */
export interface GreatProkimenSources {
  type: 'great_prokimen';
  position?: 'vespers' | 'presanctified';
}

/** Canon: type + number of odes */
export interface CanonSources {
  type: 'canon';
  odes?: 8 | 9;
}

/** Kathisma: type + optional position */
export interface KathismaSources {
  type: 'kathisma';
  position?: number;
}

/** Dismissal: type + hierarchical flag */
export interface DismissalSources {
  type: 'dismissal';
  hierarchical?: boolean;
}

/** Efrem prayer: type + prostrations flag */
export interface EfremPrayerSources {
  type: 'efrem_prayer';
  with_prostrations?: boolean;
}

/** Stichera: type + position */
export interface SticheraSources {
  type: 'stichera';
  position?: string;
}

/** Triadic troparia: type + tone + day of week */
export interface TriadicTropariaSources {
  type: 'triadic_troparia';
  tone?: number;
  day_of_week?: number;
}

/** Rubric: inline text */
export interface RubricSources {
  text: string;
}

/** Union of all known variable_sources shapes */
export type VariableSources =
  | LitanySources
  | ExclamationSources
  | PsalmSources
  | PrayerSources
  | SourceMetadata
  | AntiphonSources
  | EntranceVerseSources
  | TrisagionSources
  | CherubicSources
  | MegalynarionSources
  | CommunionVerseSources
  | GodTheLordSources
  | StepennaSources
  | ProkimenSources
  | GreatProkimenSources
  | CanonSources
  | KathismaSources
  | DismissalSources
  | EfremPrayerSources
  | SticheraSources
  | TriadicTropariaSources
  | RubricSources
  | Record<string, unknown>
  | unknown[]
  | null;

async function fetcher<T>(url: string): Promise<T> {
  const res = await fetch(`${API_BASE}${url}`, { credentials: 'include' });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || res.statusText);
  }
  return res.json();
}

export function useApi<T>(path: string | null) {
  return useSWR<T>(path, fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 60000,
  });
}

export async function apiGet<T>(path: string): Promise<T> {
  return fetcher<T>(path);
}

export async function apiPost<T>(path: string, data: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || res.statusText);
  }
  return res.json();
}

export async function apiPut<T>(path: string, data: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || res.statusText);
  }
  return res.json();
}

export async function apiPatch<T>(path: string, data: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || res.statusText);
  }
  return res.json();
}

export async function apiDelete(path: string): Promise<void> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || res.statusText);
  }
}

export async function apiUpload<T>(path: string, file: File): Promise<T> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || res.statusText);
  }
  return res.json();
}

export function refreshApi(path: string) {
  mutate(path);
}

// Types
export interface LiturgicalDay {
  gregorian_date: string;
  julian_date: string;
  pascha_julian: string;
  pascha_gregorian: string;
  days_from_pascha: number;
  week_from_pascha: number;
  tone: number;
  day_of_week: number;
  day_of_week_name: string;
  period: string;
  fasting: string;
}

export interface CalendarEntryResponse {
  id: number;
  date_type: string;
  month: number | null;
  day: number | null;
  pascha_offset: number | null;
  title_ru: string;
  title_fr: string | null;
  rank: string;
  tone: number | null;
  fasting: string;
  saint_id: number | null;
  rubric: string | null;
}

export interface AssembledServiceResponse {
  date: string;
  service_type: string;
  temple_id: number;
  language: string;
  calendar_style: string;
  mode: string;
  liturgical_day: LiturgicalDay;
  season_flags: Record<string, unknown>;
  feast_rank: number;
  template_type: string;
  fixed_entries: CalendarEntryResponse[];
  movable_entries: CalendarEntryResponse[];
  blocks: ServiceBlockItem[];
  lections: Record<string, LectionItem[]>;
  patron_troparia: PatronTroparia;
}

export interface ServiceBlockItem {
  slot_key: string;
  block_order: number;
  block_type: string;
  fixed_content_key: string | null;
  variable_sources: VariableSources;
  required: boolean;
  rubric: string | null;
  condition: Record<string, unknown> | null;
  content: string | null;
  title: string | null;
  book_code?: string;
  tone?: string;
  content_translated?: string;
  title_translated?: string;
  is_hierarchical?: boolean;
  is_hierarchical_modified?: boolean;
  is_fallback?: boolean;
  resolved_lang?: string;
}

export interface LectionItem {
  lection_id: number;
  book_code: string;
  zachalo: number;
  title: string;
  short_ref: string;
  content: string | null;
  reading_order: number;
  is_paremia: boolean;
}

export interface PatronTroparia {
  has_patron: boolean;
  saint_name?: string;
  dedication_type?: string;
  troparion?: { text: string; tone: string };
  kontakion?: { text: string; tone: string };
}

export interface ServiceBlockResponse {
  id: number;
  book_code: string;
  location_key: string;
  slot: string;
  slot_order: number;
  implementation_lang: string;
  translation_group_id: string | null;
  title: string | null;
  content: string;
  tone: string | null;
  rank: number | null;
  is_doxastikon: boolean;
  is_theotokion: boolean;
  is_irmos: boolean;
  is_katabasia: boolean;
  source_ref: string | null;
  rubric: string | null;
}

/** Alias used by admin components */
export type BlockResponse = ServiceBlockResponse;

export interface SaintResponse {
  id: number;
  name_ru: string;
  name_fr: string | null;
  categories: string[];
  feast_month: number | null;
  feast_day: number | null;
  brief_life_fr: string | null;
  brief_life_ru: string | null;
  icon_url: string | null;
  troparion_fr: string | null;
  troparion_ru: string | null;
  troparion_tone: string | null;
  kontakion_fr: string | null;
  kontakion_ru: string | null;
  kontakion_tone: string | null;
}

export interface TemplateResponse {
  id: number;
  service_type: string;
  sub_type: string | null;
  implementation_lang: string;
  is_special: boolean;
  trigger_condition: Record<string, unknown> | null;
  description: string | null;
  blocks: TemplateBlockResponse[];
}

export interface TemplateBlockResponse {
  id: number;
  template_id: number;
  block_order: number;
  slot_key: string;
  block_type: string;
  fixed_content_key: string | null;
  variable_sources: VariableSources;
  required: boolean;
  rubric_key: string | null;
  typikon_ref: string | null;
  condition: Record<string, unknown> | null;
  content_preview: string | null;
  content_title: string | null;
  // Canonical structure metadata (from slot_definitions)
  slot_label: string | null;
  content_type: string | null;
  // Editability flags
  is_structure_editable: boolean;
  is_translation_editable: boolean;
  // Source language text for bilingual translation editor
  source_text: string | null;
  source_lang: string | null;
}

export interface SlotDefinitionResponse {
  id: number;
  slot_key: string;
  service_type: string;
  content_type: string;
  default_block_type: string;
  typical_order: number;
  is_common: boolean;
}

export interface ReorderItem {
  id: number;
  block_order: number;
}

export interface ReorderRequest {
  items: ReorderItem[];
}

export interface ServiceTemplateResponse {
  id: number;
  name: string;
  service_type: string;
  description: string | null;
  blocks: ServiceTemplateBlockResponse[];
}

export interface ServiceTemplateBlockResponse {
  id: number;
  template_id: number;
  block_id: number;
  block_name: string;
  block_order: number;
  is_optional: boolean;
  conditions: Record<string, unknown> | null;
  overrides: Record<string, unknown> | null;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page_size: number;
  page: number;
  pages: number;
}

export interface ImportResult {
  status: string;
  total_created: number;
  total_errors: number;
  details: Record<string, { created: number; errors: number }>;
}